import { useTranslation } from 'react-i18next';
import Price from '@/components/ui/Price';
import { formatPhone } from './onlineOrderFormat';
import { formatTicketTime } from './ticketTime';
import type { WaiterOnlineOrder } from './waiterApi';

interface Props {
  order: WaiterOnlineOrder;
  onTap: (order: WaiterOnlineOrder) => void;
}

function priceToCents(value: string): number {
  return Math.round(parseFloat(value) * 100);
}

const STATUS_LABEL_KEY: Record<string, string> = {
  received: 'waiter.notes.online.statusReceived',
  preparing: 'waiter.notes.online.statusPreparing',
  ready: 'waiter.notes.online.statusReady',
};

/**
 * Spec 2026-08-28-waiter-salle-ux-design.md §2.3 D7 — one full-width row per
 * customer take-out/delivery order in the Emporter « En ligne » section
 * (never a square tile — three pieces of info to scan). A 4px left accent in
 * `--w-online` sets it apart from the Comptoir grid above. `data-late` is
 * true only for a scheduled, not-yet-handed-over order whose pickup time has
 * already passed — `handed_over` never appears in the backend's window
 * anyway (contract §3), this guard is defensive.
 */
export default function OnlineOrderRow({ order, onTap }: Props) {
  const { t } = useTranslation();
  const late =
    order.scheduledAt !== null &&
    Date.parse(order.scheduledAt) < Date.now() &&
    order.onlineStatus !== 'handed_over';
  // No fallback to "Reçue" (review F3#5) — an unknown/terminal status (e.g.
  // `handed_over`, guarded against above, or a future kitchen state) must
  // degrade to no chip rather than lie about the order being freshly
  // received.
  const statusLabelKey = STATUS_LABEL_KEY[order.onlineStatus];
  const phone = formatPhone(order.customerPhone);

  const paid = order.paymentStatus === 'PAID';

  // The row is a wrapper, not a button, so the phone can be a real `tel:`
  // link: interactive content may not nest inside a button. The open button
  // is stretched over the whole row underneath (positioned, painted above the
  // static text) and the link is lifted above it with `relative`.
  return (
    <div
      data-testid={`online-row-${order.uuid}`}
      data-late={late}
      style={{ borderInlineStart: '4px solid var(--w-online)' }}
      className="relative w-full min-h-11 flex flex-col gap-1 px-3 py-3 border-b border-hairline bg-paper text-start"
    >
      <button
        type="button"
        data-testid={`online-open-${order.uuid}`}
        aria-label={order.consumerName}
        onClick={() => onTap(order)}
        className="absolute inset-0 w-full h-full"
      />
      {/* Line 1 — HH:MM · name · total. The name is the only element that
          can flex-shrink to zero width (review — a fixed right cluster used
          to leave it unreadable on delivery rows). */}
      <span className="flex items-center gap-2">
        <span
          className="shrink-0 font-sans text-[18px] font-bold tabular-nums"
          style={late ? { color: 'var(--w-danger)' } : undefined}
        >
          {/* Device timezone, not the restaurant's — same call-site choice as
              `ticketTime.ts`'s header docstring: the waiter's handheld is
              physically at the restaurant, so its local zone is the display
              truth (review F3#6). */}
          {order.scheduledAt ? formatTicketTime(order.scheduledAt) : '—'}
        </span>
        {/* SC-1500 — « Dès que possible » : l'heure ci-dessus est le créneau le plus proche. */}
        {order.isAsap && (
          <span
            data-testid={`online-row-asap-${order.uuid}`}
            className="shrink-0 font-sans text-sm font-semibold"
          >
            {t('checkout.slotAsap')}
          </span>
        )}
        <span
          data-testid={`online-row-name-${order.uuid}`}
          className="min-w-0 flex-1 font-sans text-[18px] font-semibold truncate"
        >
          {order.consumerName}
        </span>
        <span className="shrink-0 tabular-nums text-end">
          <Price amountCents={priceToCents(order.totalTtc)} />
        </span>
      </span>
      {/* Line 2 — phone · item count · status chip · delivery tag. */}
      <span className="flex items-center gap-1.5 flex-wrap">
        {phone && (
          <a
            data-testid={`online-row-phone-${order.uuid}`}
            href={`tel:${order.customerPhone.replace(/\s+/g, '')}`}
            className="relative font-sans text-base text-ink underline underline-offset-2 py-1 -my-1"
          >
            {phone}
          </a>
        )}
        <span className="font-sans text-sm text-ink-mute">
          {phone && '· '}
          {t('waiter.notes.online.itemsCount', { n: order.itemsCount })}
        </span>
        {statusLabelKey && (
          <span
            data-testid="online-status-chip"
            className="shrink-0 rounded-full border-2 border-ink px-2 py-0.5 font-sans text-sm font-semibold text-ink"
          >
            {t(statusLabelKey)}
          </span>
        )}
        {order.type === 'delivery' && (
          <span
            data-testid="online-row-delivery-tag"
            className="shrink-0 rounded-full px-2 py-0.5 font-sans text-sm font-bold text-white"
            style={{ backgroundColor: 'var(--w-online)' }}
          >
            {t('waiter.notes.online.delivery')}
          </span>
        )}
        {paid && (
          <span
            data-testid="online-paid-chip"
            className="shrink-0 rounded-full px-2 py-0.5 font-sans text-sm font-bold text-white"
            style={{ backgroundColor: 'var(--w-success)' }}
          >
            {t('waiter.notes.online.paidChip')}
          </span>
        )}
      </span>
    </div>
  );
}
